export type StageId =
  | 'hires'
  | 'offers'
  | 'finalInterviews'
  | 'finalsScheduled'
  | 'qualified'
  | 'screeningInterviews'
  | 'reachOuts'
  | 'approved'
  | 'applications'

export interface ConversionRates {
  offerAcceptance: number
  finalOfferRate: number
  finalShowRate: number
  qualifiedRate: number
  screeningPassRate: number
  reachOutConversionRate: number
  reachOutMultiplier: number
  approvedQualificationRate: number
}

export const DEFAULT_CONVERSION_RATES: ConversionRates = {
  offerAcceptance: 80,
  finalOfferRate: 60,
  finalShowRate: 50,
  qualifiedRate: 50,
  screeningPassRate: 70,
  reachOutConversionRate: 75,
  reachOutMultiplier: 250,
  approvedQualificationRate: 35,
}

// Raw (unrounded) funnel volumes implied by a hire target and conversion rates.
export function computeFunnelVolumes(
  estimatedHires: number,
  r: ConversionRates,
): Record<StageId, number> {
  const hires = estimatedHires
  const offers = hires / (r.offerAcceptance / 100)
  const finalInterviews = offers / (r.finalOfferRate / 100)
  const finalsScheduled = finalInterviews / (r.finalShowRate / 100)
  const qualified = finalsScheduled / (r.qualifiedRate / 100)
  const screeningInterviews = qualified / (r.screeningPassRate / 100)
  const approved = screeningInterviews / (r.reachOutConversionRate / 100)
  const reachOuts = approved * (r.reachOutMultiplier / 100)
  const applications = approved / (r.approvedQualificationRate / 100)
  return {
    hires,
    offers,
    finalInterviews,
    finalsScheduled,
    qualified,
    screeningInterviews,
    reachOuts,
    approved,
    applications,
  }
}

// Overhead assumptions driven by the funnel.
export const PRE_INTERVIEW_HOURS_PER_SCHEDULED = 0.5 // 30 minutes per scheduled
// 15 minutes for every screened applicant who never made it onto the schedule.
export const PRE_INTERVIEW_HOURS_PER_UNSCHEDULED_SCREEN = 0.25
export const INTERVIEW_HOURS_PER_INTERVIEW = 1
export const ONBOARDING_HOURS_PER_HIRE = 2
export const ADMIN_HOURLY_RATE = 30
export const LEADERSHIP_HOURLY_RATE = 75

export interface OverheadBreakdown {
  preInterviewHours: number
  interviewToOnboardingHours: number
  adminCost: number
  leadershipCost: number
}

export function computeOverhead(
  estimatedHires: number,
  rates: ConversionRates,
): OverheadBreakdown {
  const volumes = computeFunnelVolumes(estimatedHires, rates)
  const unscheduledScreens = Math.max(
    0,
    volumes.screeningInterviews - volumes.finalsScheduled,
  )
  const preInterviewHours =
    volumes.finalsScheduled * PRE_INTERVIEW_HOURS_PER_SCHEDULED +
    unscheduledScreens * PRE_INTERVIEW_HOURS_PER_UNSCHEDULED_SCREEN
  const interviewToOnboardingHours =
    volumes.finalInterviews * INTERVIEW_HOURS_PER_INTERVIEW +
    volumes.hires * ONBOARDING_HOURS_PER_HIRE
  return {
    preInterviewHours,
    interviewToOnboardingHours,
    adminCost: preInterviewHours * ADMIN_HOURLY_RATE,
    leadershipCost: interviewToOnboardingHours * LEADERSHIP_HOURLY_RATE,
  }
}
